import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  procedureExecutionService,
  computeOptimisticProgress,
  type UpdateStepDto,
  type ProcedureExecutionState,
  type StepStatus,
} from '../services/procedure-execution.service';
import { procedureExecutionKeys } from './useProcedureExecution';

export function useUpdateStep(inspectionId: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ stepExecutionId, dto }: { stepExecutionId: string; dto: UpdateStepDto }) =>
      procedureExecutionService.updateStep(inspectionId, stepExecutionId, dto),

    onMutate: async ({ stepExecutionId, dto }) => {
      await qc.cancelQueries({ queryKey: procedureExecutionKeys.execution(inspectionId) });
      const previous = qc.getQueryData<ProcedureExecutionState>(
        procedureExecutionKeys.execution(inspectionId),
      );

      if (previous) {
        const optimisticProgress = computeOptimisticProgress(
          previous.sections,
          stepExecutionId,
          dto.status,
          previous.totalSteps,
        );

        const updated: ProcedureExecutionState = {
          ...previous,
          progressPercent: optimisticProgress.toFixed(2),
          sections: previous.sections.map((sec) => ({
            ...sec,
            steps: sec.steps.map((step) =>
              step.stepExecutionId === stepExecutionId
                ? {
                    ...step,
                    status: dto.status,
                    passFailResult: dto.passFailResult ?? null,
                    reason: dto.reason ?? null,
                  }
                : step,
            ),
          })),
        };

        qc.setQueryData(procedureExecutionKeys.execution(inspectionId), updated);
      }

      return { previous };
    },

    onError: (_err, _vars, context) => {
      if (context?.previous) {
        qc.setQueryData(procedureExecutionKeys.execution(inspectionId), context.previous);
      }
    },

    onSuccess: (response, { stepExecutionId }) => {
      qc.setQueryData<ProcedureExecutionState>(
        procedureExecutionKeys.execution(inspectionId),
        (prev) => {
          if (!prev) return prev;
          const TERMINAL: StepStatus[] = ['COMPLETED', 'SKIPPED', 'NOT_APPLICABLE'];
          return {
            ...prev,
            progressPercent: response.progressPercent,
            sections: prev.sections.map((sec) => {
              const updatedSteps = sec.steps.map((step) =>
                step.stepExecutionId === stepExecutionId
                  ? {
                      ...step,
                      status: response.status,
                      passFailResult: response.passFailResult,
                      reason: response.reason,
                      executedBy: { id: response.executedBy.id, fullName: '' },
                      executedAt: response.executedAt,
                    }
                  : step,
              );
              return {
                ...sec,
                steps: updatedSteps,
                completedSteps: updatedSteps.filter((s) => TERMINAL.includes(s.status)).length,
              };
            }),
          };
        },
      );
      void qc.invalidateQueries({ queryKey: procedureExecutionKeys.execution(inspectionId) });
      // Checklist completion feeds Inspection Report readiness — refresh the gate.
      void qc.invalidateQueries({ queryKey: ['report-readiness', inspectionId] });
    },
  });
}

export function useCompleteExecution(inspectionId: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: () => procedureExecutionService.complete(inspectionId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: procedureExecutionKeys.execution(inspectionId) });
      void qc.invalidateQueries({ queryKey: ['report-readiness', inspectionId] });
    },
  });
}
